import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { Network } from "vis-network";
import { stringToColor } from "../utils/colorUtils";

const SHOW_NUM = 100;

// 使用新的颜色工具生成节点颜色
function generateNodeColors(node) {
  // 使用节点标签生成颜色，这样相同名称的节点会有相同颜色
  const backgroundColor = stringToColor(node.label);
  
  return {
    background: backgroundColor,
    border: '#ffffff',
    highlight: {
      background: backgroundColor,
      border: '#000000'
    }
  };
}

function scaleEdgeSize(count, maxSize, maxEdgeWidth) {
  return 1 + (maxEdgeWidth - 1) * (1 - Math.exp((1 - count) / maxSize * 5));
}

function mergeEdges(edges) {
  const edgeMap = new Map();
  let maxSize = 1;

  edges.forEach(({ source, target, ...rest }) => {
    const key = `${source}-${target}`;
    if (edgeMap.has(key)) {
      edgeMap.get(key).size += 1;
    } else {
      edgeMap.set(key, { source, target, size: 1, ...rest });
    }
    maxSize = Math.max(maxSize, edgeMap.get(key).size);
  });

  const maxEdgeWidth = 5;
  return Array.from(edgeMap.values()).map(edge => ({
    ...edge,
    size: scaleEdgeSize(edge.size, maxSize, maxEdgeWidth),
  }));
}

const KgVisV2 = forwardRef((props, ref) => {
  const containerRef = useRef();
  const networkRef = useRef();
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: null });
  const [labelFrequency, setLabelFrequency] = useState({});

  useImperativeHandle(ref, () => ({
    getNetwork: () => networkRef.current,
  }));

  useEffect(() => {
    // 计算标签频率
    const frequencies = {};
    if (props.nodes) {
      props.nodes.forEach(node => {
        frequencies[node.label] = (frequencies[node.label] || 0) + 1;
      });
    }
    setLabelFrequency(frequencies);
  }, [props.nodes]);

  useEffect(() => {
    if (!props.nodes || !props.edges || !containerRef.current) return;

    // 筛选和处理节点
    const sortedNodes = [...props.nodes].sort((a, b) => 
      (labelFrequency[b.label] || 0) - (labelFrequency[a.label] || 0)
    );
    const topNodes = sortedNodes.slice(0, SHOW_NUM);
    const topNodeIds = new Set(topNodes.map(node => node.id));

    // 筛选边
    const filteredEdges = props.edges.filter(edge => 
      topNodeIds.has(edge.source) && topNodeIds.has(edge.target)
    );
    const processedEdges = mergeEdges(filteredEdges);

    // 准备vis-network数据格式
    const nodes = topNodes.map(node => {
      const nodeColors = generateNodeColors(node);
      
      return {
        id: node.id,
        label: node.label,
        color: {
          background: nodeColors.background,
          border: nodeColors.border,
          highlight: nodeColors.highlight
        },
        font: {
          color: '#333333',
          size: 14,
          face: 'Arial',
          strokeWidth: 2,
          strokeColor: '#ffffff'
        },
        size: Math.max(15, Math.min(30, (labelFrequency[node.label] || 0) * 3 + 15)),
        // 保存原始数据用于回调
        originalData: node
      };
    });

    const edges = processedEdges.map((edge, index) => {
      console.log(edge);
      const isAssociated = edge.label === 'associated_with';
      
      return {
        id: `edge_${index}`,
        from: edge.source,
        to: edge.target,
        width: edge.size,
        label: edge.label || '',
        color: {
          color: '#999999',
          opacity: 1
        },
        arrows: isAssociated ? {
          to: {
            enabled: true,
            scaleFactor: 0.8
          },
          from: {
            enabled: true,
            scaleFactor: 0.8
          }
        } : {
          to: {
            enabled: true,
            scaleFactor: 0.8
          }
        },
        font: {
          color: '#333333',
          size: 12,
          face: 'Arial',
          strokeWidth: 1,
          strokeColor: '#ffffff',
          align: 'middle'
        },
        smooth: {
          enabled: true,
          type: 'continuous',
          roundness: 0.2
        }
      };
    });

    const data = { nodes, edges };

    // vis-network配置选项
    const options = {
             layout: {
         improvedLayout: true,
         clusterThreshold: 150,
         hierarchical: {
           enabled: false,
           levelSeparation: 300,
           nodeSpacing: 200,
           treeSpacing: 300,
           blockShifting: true,
           edgeMinimization: true,
           parentCentralization: true,
           direction: 'UD',
           sortMethod: 'directed'
         },
         randomSeed: 2
       },
             physics: {
         enabled: true,
         stabilization: {
           enabled: true,
           iterations: 1000,
           updateInterval: 100,
           onlyDynamicEdges: false,
           fit: true
         },
         barnesHut: {
           gravitationalConstant: -4000,
           centralGravity: 0.1,
           springLength: 200,
           springConstant: 0.02,
           damping: 0.95,
           avoidOverlap: 0.5
         },
         maxVelocity: 50,
         minVelocity: 0.75,
         solver: 'barnesHut',
         timestep: 0.5,
         adaptiveTimestep: true
       },
      interaction: {
        dragNodes: true,
        dragView: true,
        zoomView: true,
        selectConnectedEdges: false,
        hover: true,
        hoverConnectedEdges: false,
        tooltipDelay: 200
      },
      nodes: {
        shape: 'dot',
        borderWidth: 2,
        shadow: {
          enabled: true,
          color: 'rgba(0,0,0,0.2)',
          size: 5,
          x: 2,
          y: 2
        }
      },
      edges: {
         shadow: {
           enabled: true,
           color: 'rgba(0,0,0,0.3)',
           size: 3,
           x: 2,
           y: 2
         },
         smooth: {
           enabled: true,
           type: 'curvedCW',
           forceDirection: 'none',
           roundness: 0.15
         }
       },
      groups: {}
    };

    // 创建网络
    const network = new Network(containerRef.current, data, options);
    networkRef.current = network;

    // 事件监听器
    network.on('click', (params) => {
      if (params.nodes.length > 0) {
        const nodeId = params.nodes[0];
        const nodeData = nodes.find(n => n.id === nodeId);
        if (nodeData && props.onNodeClick) {
          props.onNodeClick(nodeData.originalData);
        }
      }
    });

    network.on('hoverNode', (params) => {
      const nodeId = params.node;
      const nodeData = nodes.find(n => n.id === nodeId);
      if (nodeData) {
        setTooltip({
          visible: true,
          x: 20, // 固定在左上角
          y: 20, // 固定在左上角
          content: {
            label: nodeData.originalData.label,
            id: nodeData.originalData.label_id,
            type: nodeData.originalData.type || 'Unknown',
            ontology: nodeData.originalData.ontology || 'Unknown',
            frequency: labelFrequency[nodeData.originalData.label] || 0
          }
        });
      }
    });

         network.on('blurNode', () => {
       setTooltip(prev => ({ ...prev, visible: false }));
     });

     // 拖拽开始时禁用物理引擎的稳定化
     network.on('dragStart', () => {
       network.setOptions({
         physics: {
           enabled: true,
           stabilization: { enabled: false }
         }
       });
     });

     // 拖拽结束时重新启用稳定化，实现"弹一下"效果
     network.on('dragEnd', () => {
       network.setOptions({
         physics: {
           enabled: true,
           stabilization: {
             enabled: true,
             iterations: 150,
             updateInterval: 25
           }
         }
       });
     });

    // 清理函数
    return () => {
      if (networkRef.current) {
        networkRef.current.destroy();
        networkRef.current = null;
      }
    };

  }, [props.nodes, props.edges, labelFrequency, props.onNodeClick]);

  return (
    <div className="kgvis-box" style={{ position: 'relative', width: '100%', height: '600px' }}>
      <div
        ref={containerRef}
        style={{ 
          width: '100%', 
          height: '100%',
          border: '1px solid #ddd',
          borderRadius: '4px'
        }}
      />
      {tooltip.visible && (
        <div style={{
          position: 'absolute',
          left: tooltip.x,
          top: tooltip.y,
          background: 'white',
          padding: '8px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          zIndex: 1000,
          pointerEvents: 'none',
          fontSize: '12px'
        }}>
          <div><strong>Name:</strong> {tooltip.content.label}</div>
          <div><strong>Type:</strong> {tooltip.content.type}</div>
          <div><strong>Ontology:</strong> {tooltip.content.ontology}</div>
          <div><strong>ID:</strong> {tooltip.content.id}</div>
          <div><strong>Frequency:</strong> {tooltip.content.frequency}</div>
        </div>
      )}
    </div>
  );
});

export default KgVisV2; 